/** Client-side Yahoo daily OHLC for trade detail charts (public chart API). */

export type YahooDailyCandle = {
  time: number
  open: number
  high: number
  low: number
  close: number
}

export type YahooSymbolOptions = {
  currency?: string | null
  exchange?: string | null
  micCode?: string | null
}

const US_EXCHANGES = new Set(['NASDAQ', 'NYSE', 'AMEX', 'XNAS', 'XNYS', 'XASE', 'ARCX'])
const EXCHANGE_SUFFIX: Record<string, string> = {
  BIST: '.IS',
  XIST: '.IS',
  LSE: '.L',
  XLON: '.L',
  XETRA: '.DE',
  XETR: '.DE',
  TSE: '.T',
  XTKS: '.T',
  HKEX: '.HK',
  XHKG: '.HK',
  ASX: '.AX',
  XASX: '.AX',
  TSX: '.TO',
  XTSE: '.TO',
}

function yahooSuffixForExchange(exchange?: string | null, micCode?: string | null): string {
  const candidates = [micCode, exchange]
    .filter((value): value is string => Boolean(value?.trim()))
    .map((value) => value.trim().toUpperCase())
  for (const key of candidates) {
    if (US_EXCHANGES.has(key) || key.includes('NASDAQ') || key.includes('NEW YORK')) return ''
    if (EXCHANGE_SUFFIX[key]) return EXCHANGE_SUFFIX[key]
  }
  return ''
}

function normalizeQuoteCurrency(currency?: string | null): string {
  return (currency || 'USD').replace(/^\$/, '').toUpperCase() || 'USD'
}

export function toYahooSymbol(symbol: string, market: string, options?: YahooSymbolOptions): string {
  const raw = symbol.trim()
  if (!raw) return raw
  if (market === 'Crypto') {
    if (raw.includes('-')) return raw.toUpperCase()
    if (raw.includes('/')) return raw.replace('/', '-').toUpperCase()
    return `${raw.toUpperCase()}-${normalizeQuoteCurrency(options?.currency)}`
  }
  if (market === 'Forex') {
    const upper = raw.toUpperCase()
    return upper.endsWith('=X') ? upper : `${upper.replace('/', '').replace('-', '')}=X`
  }
  const upper = raw.toUpperCase()
  if (upper.endsWith('=F') || upper.endsWith('=X')) return raw
  const suffix = yahooSuffixForExchange(options?.exchange, options?.micCode)
  if (suffix) return `${raw}${suffix}`
  if (/^[A-Z0-9]+\.[A-Z]$/i.test(raw)) return raw.replace('.', '-')
  return raw
}

function startOfUtcDay(ms: number): number {
  const d = new Date(ms)
  return Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate())
}

function parseYahooDailyCandles(data: Record<string, unknown>): YahooDailyCandle[] {
  const chart = data.chart as Record<string, unknown> | undefined
  const result = (chart?.result as Array<Record<string, unknown>> | undefined)?.[0]
  if (!result) return []

  const timestamps = (result.timestamp as number[] | undefined) ?? []
  const quote = (result.indicators as { quote?: Array<Record<string, unknown>> } | undefined)?.quote?.[0]
  if (!quote) return []

  const opens = (quote.open as (number | null)[] | undefined) ?? []
  const highs = (quote.high as (number | null)[] | undefined) ?? []
  const lows = (quote.low as (number | null)[] | undefined) ?? []
  const closes = (quote.close as (number | null)[] | undefined) ?? []

  const candles: YahooDailyCandle[] = []
  for (let i = 0; i < timestamps.length; i++) {
    const close = closes[i]
    if (close == null || !Number.isFinite(close) || close <= 0) continue
    const open = opens[i] ?? close
    const high = highs[i] ?? Math.max(open, close)
    const low = lows[i] ?? Math.min(open, close)
    candles.push({
      time: startOfUtcDay(timestamps[i] * 1000),
      open,
      high: Math.max(high, open, close, low),
      low: Math.min(low, open, close, high),
      close,
    })
  }
  return candles.sort((a, b) => a.time - b.time)
}

export async function fetchYahooDailyCandles(
  symbol: string,
  market: string,
  periodStartMs: number,
  periodEndMs: number,
  options?: YahooSymbolOptions
): Promise<YahooDailyCandle[]> {
  const yahooSymbol = toYahooSymbol(symbol, market, options)
  const period1 = Math.floor(periodStartMs / 1000) - 86400
  const period2 = Math.floor(periodEndMs / 1000) + 86400
  const params = new URLSearchParams({
    symbol: yahooSymbol,
    period1: String(period1),
    period2: String(period2),
  })
  const response = await fetch(`/api/yahoo-chart?${params}`)
  if (!response.ok) throw new Error(`Yahoo HTTP ${response.status}`)
  const data = (await response.json()) as Record<string, unknown>
  const candles = parseYahooDailyCandles(data)
  if (!candles.length) throw new Error('No daily bars returned')
  return candles
}
